import type { MegaMenuHeaderRuntimePayload } from "@multi-tenant-cms/types/menu";

/** sessionStorage key — restore open mega tab after audience hub navigation. */
export const MMH_OPEN_MEGA_TAB_LABEL_KEY = "mmh-open-mega-tab-label";

type MegaTabRoot = Extract<
	MegaMenuHeaderRuntimePayload["roots"][number],
	{ itemKind: "mega_tab" }
>;

function normalizeLabel(label: string): string {
	return label.trim().toLowerCase();
}

/** Stash before `location.assign` so the panel can reopen on the next page. */
export function stashOpenMegaTabLabel(label: string): void {
	const trimmed = label.trim();
	if (!trimmed) {
		return;
	}
	try {
		sessionStorage.setItem(MMH_OPEN_MEGA_TAB_LABEL_KEY, trimmed);
	} catch {
		// ignore quota / private mode
	}
}

/** Read + clear a stashed label (one-shot after navigation). */
export function consumeOpenMegaTabLabel(): string | null {
	try {
		const value = sessionStorage.getItem(MMH_OPEN_MEGA_TAB_LABEL_KEY);
		if (value) {
			sessionStorage.removeItem(MMH_OPEN_MEGA_TAB_LABEL_KEY);
		}
		const trimmed = value?.trim() ?? "";
		return trimmed || null;
	} catch {
		return null;
	}
}

/** Match a mega tab in another audience menu by visible label. */
export function findMegaTabByLabel(
	roots: MegaMenuHeaderRuntimePayload["roots"],
	label: string
): MegaTabRoot | null {
	const want = normalizeLabel(label);
	if (!want) {
		return null;
	}
	for (const root of roots) {
		if (root.itemKind === "mega_tab" && normalizeLabel(root.label) === want) {
			return root;
		}
	}
	return null;
}
